import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { PerformanceNoticesAdmin } from '../../components/staff/PerformanceNoticesAdmin';
import { Plus, Pencil, Trash2, Users, FileText, Download, UserPlus, AlertTriangle, Truck, Divide } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const R = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EMPTY_STAFF = { full_name: '', username: '', password: '', phone: '', id_number: '', position: 'Internal Staff', default_basic_salary: 0, loan_balance: 0 };
const EMPTY_SLIP = {
  staff_id: '', month: new Date().toISOString().slice(0, 7),
  basic_salary: 0, days_worked: 22, standard_days: 22, deliveries_count: 0,
  paid_leave_days: 0, unpaid_leave_days: 0,
  allowance: 0, allowance_note: '',
  incentive: 0, incentive_note: '',
  performance_incentive: 0, performance_target: '',
  company_perks: 0, company_perks_note: 'Travelling',
  loan_opening_balance: 0, loan_deduction: 0,
  deductions: 0, deductions_note: '', notes: '',
};

export default function InternalStaffAdmin() {
  const [staff, setStaff] = useState([]);
  const [slips, setSlips] = useState([]);
  const [staffForm, setStaffForm] = useState(EMPTY_STAFF);
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);

  const [slipForm, setSlipForm] = useState(EMPTY_SLIP);
  const [showSlipForm, setShowSlipForm] = useState(false);
  const [editingSlip, setEditingSlip] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Collection divisors (per-driver) — fetched from /driver-trips/collection-divisors.
  // Keyed by user_id for O(1) lookup when rendering the staff table.
  const [divisorMap, setDivisorMap] = useState({});
  const [savingDivisorId, setSavingDivisorId] = useState(null);

  const loadDivisors = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/driver-trips/collection-divisors`, { headers });
      const map = {};
      for (const u of (r.data?.users || [])) {
        map[u.id] = u.collection_divisor || 1;
      }
      setDivisorMap(map);
    } catch {
      // Non-fatal: endpoint only exists for admins; staff table still renders without it
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDivisor = async (staffId, newValue) => {
    const n = parseInt(newValue, 10);
    if (!Number.isInteger(n) || n < 1 || n > 20) {
      toast.error('Divisor must be a whole number from 1 to 20');
      return;
    }
    setSavingDivisorId(staffId);
    try {
      await axios.put(
        `${API_URL}/api/driver-trips/collection-divisor/${staffId}`,
        { collection_divisor: n },
        { headers },
      );
      setDivisorMap(prev => ({ ...prev, [staffId]: n }));
      toast.success(n === 1 ? 'Set to normal driver (÷1)' : `Collection divisor set to ÷${n}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update divisor');
    } finally {
      setSavingDivisorId(null);
    }
  };

  const fetchAll = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API_URL}/api/internal-staff`, { headers }),
        axios.get(`${API_URL}/api/internal-staff/payslips`, { headers }),
      ]);
      setStaff(s.data); setSlips(p.data);
    } catch { toast.error('Failed to load'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { fetchAll(); loadDivisors(); }, [fetchAll, loadDivisors]);

  // --- Staff handlers
  const openStaffNew = () => { setEditingStaff(null); setStaffForm(EMPTY_STAFF); setShowStaffForm(true); };
  const openStaffEdit = (s) => { setEditingStaff(s); setStaffForm({ ...EMPTY_STAFF, ...s, password: '' }); setShowStaffForm(true); };
  const saveStaff = async () => {
    try {
      if (editingStaff) {
        await axios.put(`${API_URL}/api/internal-staff/${editingStaff.id}`, staffForm, { headers });
        toast.success('Staff updated');
      } else {
        await axios.post(`${API_URL}/api/internal-staff`, staffForm, { headers });
        toast.success('Staff created');
      }
      setShowStaffForm(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const delStaff = async (s) => {
    if (!window.confirm(`Delete ${s.full_name} and all their payslips?`)) return;
    try { await axios.delete(`${API_URL}/api/internal-staff/${s.id}`, { headers }); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Delete failed'); }
  };

  // --- Payslip handlers
  const openSlipNew = (staffId = '') => {
    const st = staff.find(x => x.id === staffId);
    setEditingSlip(null);
    setSlipForm({
      ...EMPTY_SLIP,
      staff_id: staffId,
      basic_salary: st?.default_basic_salary || 0,
      loan_opening_balance: st?.loan_balance || 0,
    });
    setShowSlipForm(true);
  };
  const openSlipEdit = (s) => { setEditingSlip(s); setSlipForm({ ...s }); setShowSlipForm(true); };
  const saveSlip = async () => {
    if (!slipForm.staff_id) { toast.error('Select a staff member'); return; }
    try {
      if (editingSlip) {
        await axios.put(`${API_URL}/api/internal-staff/payslips/${editingSlip.id}`, slipForm, { headers });
        toast.success('Payslip updated');
      } else {
        await axios.post(`${API_URL}/api/internal-staff/payslips`, slipForm, { headers });
        toast.success('Payslip created');
      }
      setShowSlipForm(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const delSlip = async (s) => {
    if (!window.confirm(`Delete payslip for ${s.staff_name} (${s.month})?`)) return;
    try { await axios.delete(`${API_URL}/api/internal-staff/payslips/${s.id}`, { headers }); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Delete failed'); }
  };
  const downloadSlip = async (s) => {
    try {
      const res = await axios.get(`${API_URL}/api/internal-staff/payslips/${s.id}/pdf`, { headers, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `JSM_Payslip_${(s.staff_name || '').replace(/\s+/g, '_')}_${s.month}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  // Compute live preview of net pay in dialog (mirrors backend _compute)
  const preview = (() => {
    const basicSalary = parseFloat(slipForm.basic_salary) || 0;
    const daysWorked = parseFloat(slipForm.days_worked) || 0;
    const standardDays = parseFloat(slipForm.standard_days) || 0;
    const paidLeave = parseFloat(slipForm.paid_leave_days) || 0;
    const unpaidLeave = parseFloat(slipForm.unpaid_leave_days) || 0;
    const allowance = parseFloat(slipForm.allowance) || 0;
    const incentive = parseFloat(slipForm.incentive) || 0;
    const performanceIncentive = parseFloat(slipForm.performance_incentive) || 0;
    const loan = parseFloat(slipForm.loan_deduction) || 0;
    const other = parseFloat(slipForm.deductions) || 0;
    const perks = parseFloat(slipForm.company_perks) || 0;

    const paidDaysTotal = daysWorked + paidLeave;
    const dailyRate = standardDays > 0 ? Math.round((basicSalary / standardDays) * 100) / 100 : 0;
    const effectiveBasic = (standardDays > 0 && paidDaysTotal > 0)
      ? Math.round((basicSalary * (Math.min(paidDaysTotal, standardDays) / standardDays)) * 100) / 100
      : basicSalary;
    const extraDays = Math.max(0, paidDaysTotal - standardDays);
    const extraDaysPay = Math.round(extraDays * dailyRate * 100) / 100;
    const unpaidLeaveAmount = Math.round(unpaidLeave * dailyRate * 100) / 100;
    const proRated = Math.abs(effectiveBasic - basicSalary) > 0.01 || extraDays > 0;

    const gross = effectiveBasic + extraDaysPay + allowance + incentive + performanceIncentive - unpaidLeaveAmount;
    const net = gross - loan - other;
    return {
      basicSalary, daysWorked, standardDays, effectiveBasic, proRated,
      paidLeave, unpaidLeave, paidDaysTotal, dailyRate, extraDays, extraDaysPay, unpaidLeaveAmount,
      allowance, incentive, performanceIncentive, loan, other, perks,
      gross,
      net,
      costToCompany: net + loan + perks,
      loanOpening: parseFloat(slipForm.loan_opening_balance) || 0,
      loanClosing: Math.max((parseFloat(slipForm.loan_opening_balance) || 0) - loan, 0),
    };
  })();

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Users className="h-7 w-7 text-blue-600" />
              Internal Staff &amp; Payslips
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Create staff login accounts and issue monthly payslips (days, rate, incentive, deductions).</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={openStaffNew} variant="outline" data-testid="new-staff-btn"><UserPlus className="h-4 w-4 mr-1" /> New Staff</Button>
            <Button onClick={() => openSlipNew(staff[0]?.id || '')} className="bg-blue-600 hover:bg-blue-700" data-testid="new-slip-btn">
              <Plus className="h-4 w-4 mr-1" /> Issue Payslip
            </Button>
          </div>
        </div>

        <PerformanceNoticesAdmin />

        {/* Assignment status banner — every internal driver must have a vehicle. */}
        {(() => {
          const missing = staff.filter((s) => !s.assigned_vehicle_registration);
          if (staff.length === 0) return null;
          if (missing.length === 0) {
            return (
              <Card className="border-emerald-200 bg-emerald-50" data-testid="vehicle-assignment-ok">
                <CardContent className="py-2 px-4 flex items-center gap-2 text-emerald-800 text-sm">
                  <Truck className="h-4 w-4" />
                  All {staff.length} internal staff have a vehicle assigned.
                </CardContent>
              </Card>
            );
          }
          return (
            <Card className="border-amber-300 bg-amber-50" data-testid="vehicle-assignment-warning">
              <CardContent className="py-3 px-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5" />
                <div className="flex-1 text-sm">
                  <div className="font-semibold text-amber-900">
                    {missing.length} internal staff without an assigned vehicle
                  </div>
                  <div className="text-amber-800 text-xs mt-0.5">
                    Every internal driver should be linked to a fleet vehicle so it follows them across the app.
                    Assign one from Fleet Maintenance → Edit vehicle → Assigned Driver.
                  </div>
                  <div className="mt-1 text-xs text-amber-900">
                    <strong>Missing:</strong> {missing.map((m) => m.full_name).join(', ')}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Staff table */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Staff ({staff.length})</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {staff.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No staff yet. Click "New Staff" to add one.</div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Name</TableHead><TableHead>Username</TableHead><TableHead>Position</TableHead>
                  <TableHead>Assigned Vehicle</TableHead>
                  <TableHead>Phone</TableHead><TableHead className="text-right">Daily Rate</TableHead>
                  <TableHead className="text-center" title="Divide collected parcels by this number before counting (default 1 = normal, 3 = route sweeper)">Collection ÷</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {staff.map(s => (
                    <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                      <TableCell className="font-medium">{s.full_name}</TableCell>
                      <TableCell className="font-mono text-xs">{s.username}</TableCell>
                      <TableCell>{s.position || 'Delivery Driver'}</TableCell>
                      <TableCell>
                        {s.assigned_vehicle_registration ? (
                          <div className="text-sm">
                            <div className="font-semibold text-indigo-700">{s.assigned_vehicle_registration}</div>
                            {s.assigned_vehicle_name && <div className="text-[10px] text-slate-500">{s.assigned_vehicle_name}</div>}
                          </div>
                        ) : (
                          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 gap-1" data-testid={`staff-missing-vehicle-${s.id}`}>
                            <AlertTriangle className="h-3 w-3" /> No vehicle
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{s.phone || '-'}</TableCell>
                      <TableCell className="text-right">{R(s.default_daily_rate)}</TableCell>
                      <TableCell className="text-center" data-testid={`staff-divisor-${s.id}`}>
                        <div className="inline-flex items-center gap-1">
                          <Divide className={`h-3 w-3 ${(divisorMap[s.id] || 1) > 1 ? 'text-indigo-600' : 'text-slate-300'}`} />
                          <Input
                            type="number" min={1} max={20}
                            defaultValue={divisorMap[s.id] ?? 1}
                            key={`div-${s.id}-${divisorMap[s.id] ?? 1}`}
                            onBlur={(e) => {
                              const n = parseInt(e.target.value, 10);
                              if (!Number.isInteger(n) || n < 1 || n > 20) {
                                e.target.value = String(divisorMap[s.id] ?? 1);
                                return;
                              }
                              if (n !== (divisorMap[s.id] ?? 1)) saveDivisor(s.id, n);
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            disabled={savingDivisorId === s.id}
                            className={`w-16 h-7 text-center font-semibold text-sm ${(divisorMap[s.id] || 1) > 1 ? 'border-indigo-400 bg-indigo-50 text-indigo-900' : ''}`}
                            data-testid={`staff-divisor-input-${s.id}`}
                            title={(divisorMap[s.id] || 1) === 1 ? 'Normal driver — collected parcels count as-is' : `Collection route — 90 collected ÷ ${divisorMap[s.id]} = ${Math.floor(90 / (divisorMap[s.id] || 1))} effective`}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => openSlipNew(s.id)} className="mr-1 h-8"><Plus className="h-3 w-3 mr-1" />Payslip</Button>
                        <Button size="icon" variant="ghost" onClick={() => openStaffEdit(s)}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => delStaff(s)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Payslips table */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Payslips ({slips.length})</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {slips.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No payslips issued yet.</div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Month</TableHead><TableHead>Staff</TableHead>
                  <TableHead className="text-center">Days</TableHead>
                  <TableHead className="text-center">Deliveries</TableHead>
                  <TableHead className="text-right">Basic</TableHead>
                  <TableHead className="text-right">Incentive</TableHead>
                  <TableHead className="text-right">Deductions</TableHead>
                  <TableHead className="text-right">Net Pay</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {slips.map(s => (
                    <TableRow key={s.id} data-testid={`slip-row-${s.id}`}>
                      <TableCell className="font-mono text-xs">{s.month}</TableCell>
                      <TableCell className="font-medium">{s.staff_name || '—'}</TableCell>
                      <TableCell className="text-center">{s.days_worked}</TableCell>
                      <TableCell className="text-center">{s.deliveries_count}</TableCell>
                      <TableCell className="text-right">{R(s.basic_pay)}</TableCell>
                      <TableCell className="text-right text-emerald-600">{R(s.incentive)}</TableCell>
                      <TableCell className="text-right text-rose-600">-{R(s.deductions)}</TableCell>
                      <TableCell className="text-right font-extrabold"><Badge className="bg-indigo-600">{R(s.net_pay)}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button size="icon" variant="ghost" onClick={() => downloadSlip(s)} title="Download PDF"><Download className="h-4 w-4 text-blue-600" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => openSlipEdit(s)}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => delSlip(s)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Staff dialog */}
      <Dialog open={showStaffForm} onOpenChange={setShowStaffForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingStaff ? 'Edit Staff' : 'New Staff'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full Name</Label><Input value={staffForm.full_name} onChange={(e) => setStaffForm({ ...staffForm, full_name: e.target.value })} data-testid="staff-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Username</Label><Input value={staffForm.username} onChange={(e) => setStaffForm({ ...staffForm, username: e.target.value })} disabled={!!editingStaff} data-testid="staff-username" /></div>
              <div><Label>{editingStaff ? 'New password (leave blank to keep)' : 'Password'}</Label><Input type="text" value={staffForm.password} onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })} data-testid="staff-password" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={staffForm.phone} onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })} /></div>
              <div><Label>ID Number</Label><Input value={staffForm.id_number} onChange={(e) => setStaffForm({ ...staffForm, id_number: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Position</Label><Input value={staffForm.position} onChange={(e) => setStaffForm({ ...staffForm, position: e.target.value })} /></div>
              <div><Label>Default Basic Salary (R)</Label><Input type="number" min="0" step="0.01" value={staffForm.default_basic_salary} onChange={(e) => setStaffForm({ ...staffForm, default_basic_salary: parseFloat(e.target.value) || 0 })} data-testid="staff-basic-salary" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div></div>
              <div><Label>Current Loan Balance (R)</Label><Input type="number" min="0" step="0.01" value={staffForm.loan_balance} onChange={(e) => setStaffForm({ ...staffForm, loan_balance: parseFloat(e.target.value) || 0 })} data-testid="staff-loan-balance" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStaffForm(false)}>Cancel</Button>
            <Button onClick={saveStaff} className="bg-blue-600 hover:bg-blue-700" data-testid="save-staff-btn">{editingStaff ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payslip dialog */}
      <Dialog open={showSlipForm} onOpenChange={setShowSlipForm}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editingSlip ? 'Edit Payslip' : 'Issue Payslip'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Staff Member</Label>
              <select
                className="w-full border rounded px-3 py-2 bg-background"
                value={slipForm.staff_id}
                disabled={!!editingSlip}
                onChange={(e) => {
                  const st = staff.find(x => x.id === e.target.value);
                  setSlipForm({
                    ...slipForm,
                    staff_id: e.target.value,
                    basic_salary: slipForm.basic_salary || st?.default_basic_salary || 0,
                    loan_opening_balance: slipForm.loan_opening_balance || st?.loan_balance || 0,
                  });
                }}
                data-testid="slip-staff-select"
              >
                <option value="">— Select staff —</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.full_name} ({s.username})</option>)}
              </select>
            </div>
            <div><Label>Month (YYYY-MM)</Label><Input type="month" value={slipForm.month} onChange={(e) => setSlipForm({ ...slipForm, month: e.target.value })} disabled={!!editingSlip} data-testid="slip-month" /></div>
            <div><Label>Deliveries for the month</Label><Input type="number" min="0" value={slipForm.deliveries_count} onChange={(e) => setSlipForm({ ...slipForm, deliveries_count: parseInt(e.target.value) || 0 })} data-testid="slip-deliveries" /></div>
            <div><Label>Basic Salary (R)</Label><Input type="number" min="0" step="0.01" value={slipForm.basic_salary} onChange={(e) => setSlipForm({ ...slipForm, basic_salary: parseFloat(e.target.value) || 0 })} data-testid="slip-basic-salary" /></div>
            <div>
              <Label>Days Worked</Label>
              <div className="flex gap-1">
                <Input type="number" min="0" step="0.5" value={slipForm.days_worked} onChange={(e) => setSlipForm({ ...slipForm, days_worked: parseFloat(e.target.value) || 0 })} data-testid="slip-days-worked" />
                <Button type="button" variant="outline" size="sm" className="shrink-0" data-testid="pull-from-register-btn"
                  onClick={async () => {
                    if (!slipForm.staff_id) { toast.error('Pick a staff member first'); return; }
                    if (!slipForm.month) { toast.error('Pick a month first'); return; }
                    try {
                      const r = await axios.get(`${API_URL}/api/staff-register/summary`, { headers, params: { month: slipForm.month, staff_id: slipForm.staff_id } });
                      const t = r.data?.totals || {};
                      const worked = t.worked || 0;              // ON + OT + SAT + PH (PH counts as work)
                      const paidLeave = t.leave_paid || 0;
                      const unpaidLeave = t.leave_unpaid || 0;
                      setSlipForm(f => ({
                        ...f,
                        days_worked: worked,
                        paid_leave_days: paidLeave,
                        unpaid_leave_days: unpaidLeave,
                      }));
                      const parts = [`${worked} worked (incl. ${t.ph || 0} PH, ${t.ot || 0} OT, ${t.sat || 0} SAT)`];
                      if (paidLeave || unpaidLeave) parts.push(`${paidLeave} paid · ${unpaidLeave} unpaid leave`);
                      toast.success('Pulled from register: ' + parts.join(' · '));
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Pull failed');
                    }
                  }}
                >Pull from Register</Button>
              </div>
            </div>
            <div><Label>Standard Days (full month)</Label><Input type="number" min="1" step="0.5" value={slipForm.standard_days} onChange={(e) => setSlipForm({ ...slipForm, standard_days: parseFloat(e.target.value) || 0 })} placeholder="e.g. 22" data-testid="slip-standard-days" /></div>
            <div className="col-span-1 flex items-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                data-testid="pull-leave-btn"
                onClick={async () => {
                  if (!slipForm.staff_id) { toast.error('Pick a staff member first'); return; }
                  if (!slipForm.month) { toast.error('Pick a month first'); return; }
                  try {
                    const r = await axios.get(`${API_URL}/api/leave/day-classification/${slipForm.staff_id}`, { headers, params: { month: slipForm.month } });
                    const paid = r.data?.totals?.paid || 0;
                    const unpaid = r.data?.totals?.unpaid || 0;
                    setSlipForm(f => ({ ...f, paid_leave_days: paid, unpaid_leave_days: unpaid }));
                    toast.success(`Pulled ${paid} paid · ${unpaid} unpaid leave day${paid + unpaid === 1 ? '' : 's'}`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Pull failed');
                  }
                }}
              >Pull from Leave Manager</Button>
            </div>
            <div>
              <Label>Paid Leave Days</Label>
              <Input type="number" min="0" step="0.5" value={slipForm.paid_leave_days}
                onChange={(e) => setSlipForm({ ...slipForm, paid_leave_days: parseFloat(e.target.value) || 0 })}
                placeholder="Approved leave within balance"
                data-testid="slip-paid-leave-days" />
            </div>
            <div>
              <Label>Unpaid Leave Days</Label>
              <Input type="number" min="0" step="0.5" value={slipForm.unpaid_leave_days}
                onChange={(e) => setSlipForm({ ...slipForm, unpaid_leave_days: parseFloat(e.target.value) || 0 })}
                placeholder="Leave beyond balance"
                data-testid="slip-unpaid-leave-days" />
            </div>
            <div><Label>Allowance (R)</Label><Input type="number" min="0" step="0.01" value={slipForm.allowance} onChange={(e) => setSlipForm({ ...slipForm, allowance: parseFloat(e.target.value) || 0 })} data-testid="slip-allowance" /></div>
            <div><Label>Allowance Note</Label><Input placeholder="e.g. Cellphone allowance" value={slipForm.allowance_note} onChange={(e) => setSlipForm({ ...slipForm, allowance_note: e.target.value })} data-testid="slip-allowance-note" /></div>
            <div><Label>Incentive (R)</Label><Input type="number" min="0" step="0.01" value={slipForm.incentive} onChange={(e) => setSlipForm({ ...slipForm, incentive: parseFloat(e.target.value) || 0 })} data-testid="slip-incentive" /></div>
            <div><Label>Incentive Note *</Label><Input placeholder="Why? e.g. Customer service excellence" value={slipForm.incentive_note} onChange={(e) => setSlipForm({ ...slipForm, incentive_note: e.target.value })} data-testid="slip-incentive-note" /></div>
            <div><Label>Performance Incentive (R)</Label><Input type="number" min="0" step="0.01" value={slipForm.performance_incentive} onChange={(e) => setSlipForm({ ...slipForm, performance_incentive: parseFloat(e.target.value) || 0 })} data-testid="slip-performance-incentive" placeholder="e.g. 1500" /></div>
            <div><Label>Performance Target (optional)</Label><Input placeholder='e.g. "150 deliveries" or "R50,000 sales"' value={slipForm.performance_target} onChange={(e) => setSlipForm({ ...slipForm, performance_target: e.target.value })} data-testid="slip-performance-target" /></div>
            <div><Label>Company Perks - Cost to Company (R)</Label><Input type="number" min="0" step="0.01" value={slipForm.company_perks} onChange={(e) => setSlipForm({ ...slipForm, company_perks: parseFloat(e.target.value) || 0 })} placeholder="e.g. 1000" data-testid="slip-company-perks" /></div>
            <div><Label>Perks Description</Label><Input value={slipForm.company_perks_note} onChange={(e) => setSlipForm({ ...slipForm, company_perks_note: e.target.value })} placeholder="e.g. Travelling" data-testid="slip-perks-note" /></div>
            <div><Label>Loan Opening Balance (R)</Label><Input type="number" min="0" step="0.01" value={slipForm.loan_opening_balance} onChange={(e) => setSlipForm({ ...slipForm, loan_opening_balance: parseFloat(e.target.value) || 0 })} data-testid="slip-loan-opening" /></div>
            <div><Label>Loan Repayment This Period (R)</Label><Input type="number" min="0" step="0.01" value={slipForm.loan_deduction} onChange={(e) => setSlipForm({ ...slipForm, loan_deduction: parseFloat(e.target.value) || 0 })} data-testid="slip-loan-deduction" /></div>
            <div><Label>Other Deductions (R)</Label><Input type="number" min="0" step="0.01" value={slipForm.deductions} onChange={(e) => setSlipForm({ ...slipForm, deductions: parseFloat(e.target.value) || 0 })} data-testid="slip-deductions" /></div>
            <div><Label>Other Deductions Note</Label><Input placeholder="e.g. Damaged load" value={slipForm.deductions_note} onChange={(e) => setSlipForm({ ...slipForm, deductions_note: e.target.value })} /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={slipForm.notes} onChange={(e) => setSlipForm({ ...slipForm, notes: e.target.value })} /></div>
            <div className="col-span-2 rounded-lg bg-slate-100 dark:bg-slate-800 p-3 space-y-1">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Live Preview</div>
              {preview.proRated ? (
                <div className="flex justify-between text-sm">
                  <span>Basic Salary ({preview.daysWorked}{preview.paidLeave > 0 ? ` + ${preview.paidLeave} paid leave` : ''} / {preview.standardDays} days, full = {R(preview.basicSalary)})</span>
                  <span className="font-semibold">{R(preview.effectiveBasic)}</span>
                </div>
              ) : (
                <div className="flex justify-between text-sm"><span>Basic Salary</span><span className="font-semibold">{R(preview.effectiveBasic)}</span></div>
              )}
              {preview.paidLeave > 0 && preview.paidDaysTotal <= preview.standardDays && (
                <div className="flex justify-between text-[11px] text-emerald-700 pl-4"><span>· incl. Paid Leave ({preview.paidLeave} day{preview.paidLeave === 1 ? '' : 's'})</span><span>counted</span></div>
              )}
              {preview.extraDays > 0 && (
                <div className="flex justify-between text-sm text-emerald-700"><span>+ Extra Days Pay ({preview.extraDays} day{preview.extraDays === 1 ? '' : 's'} × {R(preview.dailyRate)})</span><span className="font-semibold">{R(preview.extraDaysPay)}</span></div>
              )}
              <div className="flex justify-between text-sm"><span>+ Allowance{slipForm.allowance_note ? ` (${slipForm.allowance_note})` : ''}</span><span className="font-semibold">{R(preview.allowance)}</span></div>
              <div className="flex justify-between text-sm text-emerald-600"><span>+ Incentive{slipForm.incentive_note ? ` (${slipForm.incentive_note})` : ''}</span><span className="font-semibold">{R(preview.incentive)}</span></div>
              {(preview.performanceIncentive > 0 || slipForm.performance_target) && (
                <div className="flex justify-between text-sm text-emerald-700"><span>+ Performance Incentive{slipForm.performance_target ? ` (target: ${slipForm.performance_target})` : ''}</span><span className="font-semibold">{R(preview.performanceIncentive)}</span></div>
              )}
              {preview.unpaidLeave > 0 && (
                <div className="flex justify-between text-sm text-rose-600"><span>− Unpaid Leave ({preview.unpaidLeave} day{preview.unpaidLeave === 1 ? '' : 's'} × {R(preview.dailyRate)})</span><span className="font-semibold">-{R(preview.unpaidLeaveAmount)}</span></div>
              )}
              <div className="flex justify-between text-sm border-b pb-1"><span>= Gross Pay</span><span className="font-semibold">{R(preview.gross)}</span></div>
              <div className="flex justify-between text-sm text-rose-600"><span>− Loan Repayment</span><span className="font-semibold">-{R(preview.loan)}</span></div>
              <div className="flex justify-between text-sm text-rose-600"><span>− Other Deductions</span><span className="font-semibold">-{R(preview.other)}</span></div>
              <div className="flex justify-between text-base pt-1"><span className="font-bold">Net Pay</span><span className="font-extrabold text-indigo-600">{R(preview.net)}</span></div>
              {preview.perks > 0 && (
                <div className="mt-2 pt-2 border-t space-y-0.5">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Cost to Company</div>
                  <div className="flex justify-between text-xs text-muted-foreground"><span>+ {slipForm.company_perks_note} (perks)</span><span>{R(preview.perks)}</span></div>
                  <div className="flex justify-between text-sm"><span className="font-semibold">Total Cost to Company</span><span className="font-bold text-amber-600">{R(preview.costToCompany)}</span></div>
                </div>
              )}
              {(preview.loanOpening > 0 || preview.loan > 0) && (
                <div className="mt-2 pt-2 border-t text-xs text-muted-foreground">
                  Loan: opening {R(preview.loanOpening)} − repayment {R(preview.loan)} = closing <span className="font-semibold text-foreground">{R(preview.loanClosing)}</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSlipForm(false)}>Cancel</Button>
            <Button onClick={saveSlip} className="bg-blue-600 hover:bg-blue-700" data-testid="save-slip-btn">{editingSlip ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
